import { JOB_STATUS, JobStatusType } from '../../../constants/dentist/jobStatus';
import { Job, ProcessedJob, DateJobsMap, PartialOffer } from '../../../types/dentist/calendar';
import { isJobActionRequired } from './actionRequired';

/**
 * Filter out past unfilled shifts (matching legacy behavior)
 * Past shifts should only be kept if they are COMPLETED or CONFIRMED/BOOKED
 * This matches the legacy processJobs function which filters before processing
 */
const filterPastUnfilledShifts = (jobs: Job[]): Job[] => {
  const today = new Date();
  const currentDateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  
  return jobs.filter(job => {
    const jobDateStr = job.startDate;
    
    const isPastDate = jobDateStr < currentDateStr;
    
    if (!isPastDate) {
      return true;
    }
    return job.status === JOB_STATUS.COMPLETED || job.status === JOB_STATUS.CONFIRMED;
  });
};

/**
 * Process raw jobs data and group by date
 */
export const processJobs = (jobs: Job[]): DateJobsMap => {
  // Filter out past unfilled shifts before processing (matching legacy behavior)
  const filteredJobs = filterPastUnfilledShifts(jobs);
  const dateJobsMap: DateJobsMap = {};

  // (logging removed)

  filteredJobs.forEach(job => {
    const dateKey = job.startDate; // YYYY-MM-DD format
    
    if (!dateJobsMap[dateKey]) {
      dateJobsMap[dateKey] = [];
    }

    // Handle both uppercase and lowercase versions from API
    const partialOffers = job.PartialOffers || job.partialOffers || [];

    // Transform job status based on partial offers (matching legacy logic)
    let transformedStatus = job.status;
    let hasActiveOffers = false;
    
    // (logging removed)
    
    if (partialOffers.length > 0) {
      // Count valid partial offers (matching legacy logic exactly)
      const validPartialOffers = partialOffers.filter((offer: PartialOffer) => 
        !offer.isResubmitRequested && 
        offer.status !== 1 && // REJECTED
        offer.status !== 3 && // EXPIRED  
        offer.status !== 4    // REMOVED
      ).length;
      
      hasActiveOffers = validPartialOffers > 0;
      
      // (logging removed)
      
      // If job is PARTIAL or OFFER_EXPIRED but has no valid offers, change to POSTED
      if ((job.status === 2 || job.status === 5) && validPartialOffers === 0) {
        transformedStatus = 1; // POSTED
      }
      // If job is POSTED but has valid offers, change to PARTIAL
      else if (job.status === 1 && validPartialOffers > 0) {
        transformedStatus = 2; // PARTIAL/CUSTOM
      }

      // T2-6329: withdrawn (REMOVED) offers alone should return the shift to POSTED,
      // not OFFER_EXPIRED — only time-expired offers (status 3) imply "expired offer".
    } else {
      // dentistJobs strips REMOVED offers, so an empty partialOffers array on a
      // PARTIAL / OFFER_EXPIRED job usually means withdrawn-only (T2-6329).
      if (job.status === JOB_STATUS.CUSTOM || job.status === JOB_STATUS.EXPIRED) {
        transformedStatus = JOB_STATUS.POSTED;
      }
    }

    // Process partial offers for this job
    const hasPartialOffers = partialOffers.length > 0;
    const hasUnviewedOffers = partialOffers.some((offer: PartialOffer) => !offer.isViewed);
    const hasRemovedOffers = partialOffers.some((offer: PartialOffer) => offer.status === 4);
    
    const processedJob: ProcessedJob = {
      ...job,
      status: transformedStatus, // Use transformed status
      PartialOffers: partialOffers, // Normalize to uppercase
      hasPartialOffers,
      hasUnviewedOffers,
      hasRemovedOffers,
      hasActiveOffers, // Add the calculated hasActiveOffers
      effectiveStatus: transformedStatus // Use transformed status for effective status too
    };

    dateJobsMap[dateKey].push(processedJob);
  });

  return dateJobsMap;
};

/**
 * Determine the effective status of a job considering partial offers
 */
export const determineEffectiveStatus = (job: Job): JobStatusType => {
  // If job has partial offers and is still posted, show it as custom (status 1)
  if (job.status === JOB_STATUS.POSTED && job.PartialOffers && job.PartialOffers.length > 0) {
    return JOB_STATUS.CUSTOM;
  }
  return job.status;
};

/**
 * Get the minimum status value from an array of jobs (for primary status)
 */
export const findMinStatus = (jobs: ProcessedJob[]): number => {
  if (jobs.length === 0) return JOB_STATUS.POSTED;
  const statuses = jobs.map(job => job.effectiveStatus);
  // Legacy priority order:
  // COMPLETED (4) → EXPIRED (5) → POSTED (1) → PARTIAL/CUSTOM (2) → CONFIRMED/BOOKED (3)
  if (statuses.includes(JOB_STATUS.COMPLETED)) return JOB_STATUS.COMPLETED;
  if (statuses.includes(JOB_STATUS.EXPIRED)) return JOB_STATUS.EXPIRED;
  if (statuses.includes(JOB_STATUS.POSTED)) return JOB_STATUS.POSTED;
  if (statuses.includes(JOB_STATUS.CUSTOM)) return JOB_STATUS.CUSTOM;
  if (statuses.includes(JOB_STATUS.BOOKED)) return JOB_STATUS.BOOKED;
  return JOB_STATUS.POSTED;
};

/**
 * Get the maximum status value from an array of jobs (for secondary status)
 */
export const findMaxStatus = (jobs: ProcessedJob[]): number => {
  if (jobs.length === 0) return JOB_STATUS.POSTED;
  const statuses = jobs.map(job => job.effectiveStatus);
  // Legacy priority order for max:
  // EXPIRED (5) → CONFIRMED/BOOKED (3) → PARTIAL/CUSTOM (2) → POSTED (1) → COMPLETED (4)
  if (statuses.includes(JOB_STATUS.EXPIRED)) return JOB_STATUS.EXPIRED;
  if (statuses.includes(JOB_STATUS.BOOKED)) return JOB_STATUS.BOOKED;
  if (statuses.includes(JOB_STATUS.CUSTOM)) return JOB_STATUS.CUSTOM;
  if (statuses.includes(JOB_STATUS.POSTED)) return JOB_STATUS.POSTED;
  if (statuses.includes(JOB_STATUS.COMPLETED)) return JOB_STATUS.COMPLETED;
  return JOB_STATUS.POSTED;
};

/**
 * Generate CSS class for a date based on its jobs
 */
export const getDateStatusClass = (jobs: ProcessedJob[]): string => {
  if (!jobs || jobs.length === 0) return '';

  // Single job - simple case
  if (jobs.length === 1) {
    return `date-status-${jobs[0].effectiveStatus}`;
  }

  // Multiple jobs / statuses on one date -> split-cell combination class.
  // T2-6171/T2-6187: the combination SCSS classes are keyed by the *numeric*
  // status pair in ascending order (`date-status-1-4`, never `4-1`). The old
  // code composed `date-status-${findMinStatus}-${findMaxStatus}` using the
  // PRIORITY-ordered helpers, so a day like [POSTED(1), COMPLETED(4)] (which
  // only happens on "today", since past unfilled shifts are filtered out)
  // produced `date-status-4-1` — a class that doesn't exist, so the cell fell
  // back to a white background. It also collapsed pairs like [POSTED, EXPIRED]
  // to a solid colour. Using the numeric min/max of the distinct statuses
  // present guarantees a defined class and the correct split rendering.
  const distinctStatuses = Array.from(new Set(jobs.map(job => job.effectiveStatus)));
  const minStatus = Math.min(...distinctStatuses);
  const maxStatus = Math.max(...distinctStatuses);

  if (minStatus === maxStatus) {
    // All jobs share the same status
    return `date-status-${minStatus}`;
  }
  return `date-status-${minStatus}-${maxStatus}`;
};

/**
 * Check if any job on a date requires action
 */
export const hasActionRequired = (jobs: ProcessedJob[]): boolean => {
  // T2-6191: action required when a COMPLETED job has either an unpaid invoice
  // (existing rule) OR a pending DO feedback survey (new rule). Single red marker
  // per date — the calendar already aggregates per date so one match suffices.
  return jobs.some((job) => isJobActionRequired(job));
};

/**
 * Check if date is in the past
 */
export const isPastDate = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date < today;
};

/**
 * Get display information for a date
 */
export const getDateDisplayInfo = (date: Date, jobs: ProcessedJob[]) => {
  const isToday = isDateToday(date);
  const isPast = isPastDate(date);
  const statusClass = getDateStatusClass(jobs);
  const hasAction = hasActionRequired(jobs);
  const jobCount = jobs.length;
  // hasTarget: if there are active offers or backend provided availableNowCount > 0
  const hasTarget = jobs.some((j: any) => j.hasActiveOffers) || jobs.some((j: any) => Number((j as any).availableNowCount) > 0);

  // Count expired PartialOffers (status === 3) across all jobs on this date (T2-5764).
  // T2-6186: exclude offers the DO has already asked to resubmit
  // (isResubmitRequested) so the grey count badge matches the Custom Offers list,
  // which hides resubmit-requested offers — otherwise the badge lingers after
  // "Request Resubmit" even though the card is gone.
  const expiredOfferCount = jobs.reduce((acc: number, job: any) => {
    const partialOffers = job?.PartialOffers || job?.partialOffers || [];
    return acc + partialOffers.filter((o: any) => Number(o?.status) === 3 && !o?.isResubmitRequested).length;
  }, 0);

  return {
    isToday,
    isPast,
    statusClass,
    hasAction,
    jobCount,
    // Show indicators on all dates that have jobs (past and future)
    shouldShowIndicators: jobCount > 0,
    hasTarget,
    hasExpiredOffer: expiredOfferCount > 0,
    expiredOfferCount,
  };
};

/**
 * Check if a date is today
 */
const isDateToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}; 